import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Constant } from './constants';
import { Observable, BehaviorSubject, of, Subject, map, catchError, firstValueFrom } from 'rxjs';
import * as fromModel from './models';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ActivatedRoute } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as fromModelshared from '../../modules/shared/models';
import { MsalService } from '@azure/msal-angular';


export class UserDetail {
  name = '';
  userid = '';
  poolIds = '';
  repId = '';
  simulateRepIds = '';
  role = fromModel.USERROLE.FA;
  // True only after a super user explicitly picks a role and clicks Apply.
  roleApplied = false;
}
@Injectable({
  providedIn: 'root'
})
export class UserService {
  appInsights: ApplicationInsights = new ApplicationInsights({ config: { instrumentationKey: Constant.config.appInsightKey } });

  private sessionId = '';
  private app: string = 'Supervision Tool -> ';
  private step = 0;
  private user: UserDetail | undefined;
  private simUser: UserDetail | undefined;
    jwtHelperSvc: JwtHelperService | undefined;
    

  public userSubject = new BehaviorSubject<UserDetail | undefined>(undefined);
  public repIds$ = new BehaviorSubject<string[]>([]);
  private isdatamarkingon = false;

  constructor(private _httpClient: HttpClient,
    private route: ActivatedRoute,
      private authService: MsalService,
  ) {
    const _hash = window.location.hash;
    if (_hash && _hash.trim().toUpperCase().includes("DATAMASK=Y") || _hash.trim().toUpperCase().includes("DATAMASK='Y'")) {
      this.isdatamarkingon = true;
    }
    this.sessionId = (new Date()).getTime().toString();
    this.app += ' Session Id:' + this.sessionId + ' ';
  }



  getToken(): Observable<string | undefined> {
    return this.authService.acquireTokenSilent({
      scopes: Constant.config.scopes,
    }).pipe(
      map(tokenResponse => tokenResponse.accessToken),
      catchError(error => {
        console.error('Token Acquisition Error:', error);
        return of(undefined);
      })
    );
  }


async getTokenAsync(): Promise<string | undefined> {
  return await firstValueFrom(this.getToken());
}
  async getUserNameFromSession(): Promise<UserDetail | undefined> {
    let idtoken = sessionStorage.getItem('msal.idtoken');

    let activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount) {
      try {
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
          activeAccount = accounts[0];
          this.authService.instance.setActiveAccount(activeAccount);
        }
      } catch {
        return undefined;
      }
    }

    // Prefer MSAL account idTokenClaims for identity/roles. They are more reliable for role guard checks.
    const accountClaims = activeAccount?.idTokenClaims as Record<string, unknown> | undefined;
    if (accountClaims) {
      const userFromClaims = this.buildUserFromClaims(accountClaims);
      console.log('Logged-in user data:', {
        name: userFromClaims.name,
        userid: userFromClaims.userid,
        poolIds: userFromClaims.poolIds,
        repId: userFromClaims.repId,
        role: userFromClaims.role,
        rolesClaim: this.normalizeRolesClaim(accountClaims['roles'])
      });
      this.setUser(userFromClaims);
      return userFromClaims;
    }

    // Backward-compatible fallback: account exists but idTokenClaims are null (token expired).
    // Re-acquire a fresh token; popup is used automatically if silent acquisition times out.
    if (!activeAccount) {
      return undefined;
    }
    const freshToken = await this.getTokenAsync();
    if (!freshToken) {
      return undefined;
    }

    // After successful (re-)acquisition MSAL updates the account cache — prefer refreshed claims.
    const refreshedAccount = this.authService.instance.getActiveAccount();
    const refreshedClaims = refreshedAccount?.idTokenClaims as Record<string, unknown> | undefined;
    if (refreshedClaims) {
      const userFromClaims = this.buildUserFromClaims(refreshedClaims);
      console.log('Logged-in user data (refreshed claims):', {
        name: userFromClaims.name,
        userid: userFromClaims.userid,
        role: userFromClaims.role
      });
      this.setUser(userFromClaims);
      return userFromClaims;
    }

    // Last resort: decode the access token (may carry roles claim in this tenant's token policy).
    if (!this.jwtHelperSvc) {
      this.jwtHelperSvc = new JwtHelperService();
    }
    sessionStorage.setItem('msal.idtoken', freshToken);
    const decodedToken = this.jwtHelperSvc.decodeToken(freshToken) as Record<string, unknown>;
    const userFromToken = this.buildUserFromClaims(decodedToken);
    console.log('Logged-in user data (decoded token):', {
      name: userFromToken.name,
      userid: userFromToken.userid,
      role: userFromToken.role
    });
    this.setUser(userFromToken);
    return userFromToken;
  }

  private buildUserFromClaims(claims: Record<string, unknown>): UserDetail {
    const user = new UserDetail();
    user.name = (claims['name'] as string) || '';
    user.userid = (claims['preferred_username'] as string) || '';
    user.poolIds = (claims['poolIds'] as string) || '';
    user.repId = (claims['primaryid'] as string) || '';
    user.role = this.getRole(this.normalizeRolesClaim(claims['roles']));
    return user;
  }

  private normalizeRolesClaim(rolesClaim: unknown): string[] {
    if (Array.isArray(rolesClaim)) {
      return rolesClaim.filter((r): r is string => typeof r === 'string');
    }
    if (typeof rolesClaim === 'string' && rolesClaim.trim()) {
      return [rolesClaim.trim()];
    }
    return [];
  }

  getRole(roles: string[] | undefined): fromModel.USERROLE {
    let role = fromModel.USERROLE.FA;
    if (roles && roles.length > 0) {
      let r: string | undefined;
      r = roles.find(_role => _role === 'RCM.SUPERVISIONTECH');
      if (r !== undefined) {
        return fromModel.USERROLE.SUPER;
      }

      r = roles.find(_role => _role === 'Advisory.Compliance');
      if (r !== undefined) {
        return fromModel.USERROLE.COMP;
      }

      r = roles.find(_role => _role === 'Advisory.Supervision');
      if (r !== undefined) {
        return fromModel.USERROLE.SUPERVISION;
      }

      r = roles.find(_role => _role === 'Advisory.AML');
      if (r !== undefined) {
        return fromModel.USERROLE.AML;
      }

      r = roles.find(_role => _role === 'Advisory.HomeOffice');
      if (r !== undefined) {
        return fromModel.USERROLE.HO;
      }

      r = roles.find(_role => _role === 'Advisory.ADBD');
      if (r !== undefined) {
        return fromModel.USERROLE.BM;
      }
      
      r = roles.find(_role => _role === 'Advisory.OLT');
      if (r !== undefined) {
        return fromModel.USERROLE.BM;
      }

      r = roles.find(_role => _role === 'Advisory.All');
      if (r !== undefined) {
        return fromModel.USERROLE.FA;
      }
    }
    return role;
  }


  setUser(u: UserDetail) {
    this.user = u;
    this.userSubject.next(u);
  }

  setActiveRole(role: fromModel.USERROLE) {
    if (!this.user) {
      return;
    }

    const updatedUser = Object.assign(new UserDetail(), this.user, { role, roleApplied: true });
    this.setUser(updatedUser);
  }

  getUser(): UserDetail | undefined {
    return this.user;
  }

  // -------- Tracing -------------
  // Information, Verbose, Warning, Error, Critical
  traceMsg(msg: string, detail?: any, level: string = "Information"): void {

    this.step = this.step + 1;
    // this.appInsightsService.trackTrace(this.app + msg, {
    //   'userId': this.user?.userid || '', 'sessionId': this.sessionId, 'step': this.step.toString(),
    //   'datail': JSON.stringify(detail)
    // }, SeverityLevel.Information);

    this.appInsights.trackTrace({ message: this.app + msg }, { 'userId': this.user?.userid, 'sessionId': this.sessionId, 'datail': JSON.stringify(detail) });
    this.flushMessages();
  }

  traceException(err: Error): void {
    this.appInsights.trackException({ exception: err });
  }

  flushMessages() {
    this.appInsights.flush();
  }
  // -------- Tracing End -------

}
